import { useState, useEffect, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'

export default function ReviewsSection() {
  const { t } = useLanguage()
  const reviews = t.reviews.items
  const [index, setIndex] = useState(0)
  const [direction, setDirection] = useState(1)
  const touchStartX = useRef(0)

  const goTo = useCallback((next: number, dir: number) => {
    setIndex(next)
    setDirection(dir)
  }, [])

  const next = useCallback(() => {
    goTo((index + 1) % reviews.length, 1)
  }, [index, reviews.length, goTo])

  const prev = useCallback(() => {
    goTo((index - 1 + reviews.length) % reviews.length, -1)
  }, [index, reviews.length, goTo])

  useEffect(() => {
    const timer = setInterval(next, 10000)
    return () => clearInterval(timer)
  }, [next])

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
  }, [])

  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    const deltaX = e.changedTouches[0].clientX - touchStartX.current
    if (Math.abs(deltaX) < 50) return
    if (deltaX > 0) {
      prev()
    } else {
      next()
    }
  }, [prev, next])

  const review = reviews[index]

  return (
    <section id="recensioni" className="bg-cream px-4 py-6 md:px-8">
      <div className="relative mx-auto max-w-6xl">
        <div className="pointer-events-none absolute -top-10 right-6 z-10 w-28 select-none sm:w-32 md:-top-14 md:right-12 md:w-44">
          <motion.img
            src="/images/libri.png"
            alt=""
            aria-hidden="true"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="h-auto w-full"
          />
        </div>
        <div className="rounded-[2rem] md:rounded-[3rem] bg-ink px-4 py-16 md:px-12 md:py-28 text-cream relative overflow-hidden">
          <div
            className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-terracotta/10 blur-3xl"
            aria-hidden="true"
          />
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7 }}
            className="relative text-center"
          >
            <p className="text-sm uppercase tracking-[0.25em] text-terracotta font-medium mb-4">
              {t.reviews.eyebrow}
            </p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">
              {t.reviews.titleBefore}{' '}
              <span className="italic text-terracotta">{t.reviews.titleAccent}</span>
            </h2>
          </motion.div>

          <div
            className="relative mt-10 md:mt-16 flex items-center justify-center md:gap-0"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <button
              onClick={prev}
              aria-label={t.reviews.prevAria}
              className="hidden md:flex md:absolute md:left-2 md:z-10 md:h-12 md:w-12 md:shrink-0 md:items-center md:justify-center md:rounded-full md:border md:border-cream/20 md:bg-cream/5 md:text-cream/70 md:backdrop-blur-sm md:transition-all md:hover:border-terracotta md:hover:bg-terracotta md:hover:text-cream"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            <div className="mx-auto w-full max-w-4xl overflow-hidden px-2 md:px-16">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.figure
                  key={review.name}
                  custom={direction}
                  initial={{ opacity: 0, x: direction * 80 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: direction * -80 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="flex flex-col rounded-[1.5rem] md:rounded-[2rem] border border-cream/10 bg-cream/[0.06] p-5 md:p-12 backdrop-blur-sm"
                >
                  <figcaption className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-terracotta font-display text-lg text-cream">
                      {review.initial}
                    </span>
                    <span>
                      <span className="block font-medium text-cream">{review.name}</span>
                      <span className="block text-sm text-cream/50">{review.trip}</span>
                    </span>
                  </figcaption>
                  <blockquote className="mt-4 text-cream/85 leading-relaxed text-[15px] md:text-base">
                    {review.text}
                  </blockquote>
                </motion.figure>
              </AnimatePresence>
            </div>

            <button
              onClick={next}
              aria-label={t.reviews.nextAria}
              className="hidden md:flex md:absolute md:right-2 md:z-10 md:h-12 md:w-12 md:shrink-0 md:items-center md:justify-center md:rounded-full md:border md:border-cream/20 md:bg-cream/5 md:text-cream/70 md:backdrop-blur-sm md:transition-all md:hover:border-terracotta md:hover:bg-terracotta md:hover:text-cream"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          <div className="mt-10 flex items-center justify-center gap-2.5">
            {reviews.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i, i > index ? 1 : -1)}
                aria-label={t.reviews.goToAria.replace('{n}', String(i + 1))}
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  i === index
                    ? 'w-8 bg-terracotta'
                    : 'w-2.5 bg-cream/25 hover:bg-cream/40'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
